import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetAccessUsersQuery } from "../api/Endpoints/Core/Security/Users";
import { getCurrentUser, hasAccess } from "../helpers/session_helper";

import { coreMenuItems } from "./Components/CoreMenuItems";

// ─── Per-system menu slices ───────────────────────────────────────────────────
import { assetGuardMenuItems } from "./Components/AssetGuardMenuItems";
import { dmsMenuItems } from "./Components/DMSMenuItems";
import { fleetGuardMenuItems } from "./Components/FleetGuardMenuItems";
import { buildFmsMenuItems } from "./Components/FMSMenuItems";
import { buildHRISMenuItems } from "./Components/HRISMenuItems";
import { payrollMenuItems } from "./Components/PayrollMenuItems";
import { buildProcurementMenuItems } from "./Components/ProcumentMenuItems";

const Navdata = () => {
  const history = useNavigate();
  const location = useLocation();

  const [iscurrentState, setIscurrentState] = useState("Dashboard");

  const shouldFetch = getCurrentUser();
  const { data, refetch } = useGetAccessUsersQuery({}, { skip: !shouldFetch });
  const accessRights = data?.returnData?.map(({ permissionTypeId, allowRead, allowWrite, allowDelete }) => ({
    permissionTypeId, allowRead, allowWrite, allowDelete,
  }));

  useEffect(() => { refetch(); }, [location.pathname]);
  useEffect(() => {
    if (accessRights) sessionStorage.setItem("accessRights", JSON.stringify(accessRights));
  }, [accessRights]);

  // ── Top-level collapse states ───────────────────────────────────────────
  const [isApps, setIsApps] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const [isPages, setIsPages] = useState(false);
  const [isDashboard, setIsDashboard] = useState(false);
  const [isAssetGuard, setIsAssetGuard] = useState(false);
  const [isFleetGuard, setIsFleetGuard] = useState(false);
  const [isFMS, setIsFMS] = useState(false);
  const [isHRIS, setIsHRIS] = useState(false);
  const [isPayroll, setIsPayroll] = useState(false);
  const [isDMS, setIsDMS] = useState(false);
  const [isProcurement, setIsProcurement] = useState(false);

  // ── FMS sub-collapse ────────────────────────────────────────────────────
  const [isFMSAccounting, setIsFMSAccounting] = useState(false);
  const [isFMSBudget, setIsFMSBudget] = useState(false);
  const [isFMSCollection, setIsFMSCollection] = useState(false);
  const [isFMSDisbursement, setIsFMSDisbursement] = useState(false);

  // ── HRIS sub-collapse ─────────────────────────────────────────────
  const [isHRISSelfService, setIsHRISSelfService] = useState(false);
  const [isHRISHR, setIsHRISHR] = useState(false);
  const [isHRISRecruitment, setIsHRISRecruitment] = useState(false);
  const [isHRISWorkforce, setIsHRISWorkforce] = useState(false);
  const [isHRISTimekeeping, setIsHRISTimekeeping] = useState(false);

  // ── Procurement sub-collapse ────────────────────────────────────────────
  const [isProcPreProcurement, setIsProcPreProcurement] = useState(false);
  const [isProcBacManagement, setIsProcBacManagement] = useState(false);
  const [isProcAdvertisement, setIsProcAdvertisement] = useState(false);
  const [isProcBidEvaluation, setIsProcBidEvaluation] = useState(false);
  const [isProcPostQualification, setIsProcPostQualification] = useState(false);
  const [isProcAwardContract, setIsProcAwardContract] = useState(false);
  const [isProcImplementation, setIsProcImplementation] = useState(false);

  // ── Auto-collapse on section change ────────────────────────────────────
  useEffect(() => {
    document.body.classList.remove("twocolumn-panel");

    if (iscurrentState !== "Apps") setIsApps(false);
    if (iscurrentState !== "Auth") setIsAuth(false);
    if (iscurrentState !== "Pages") setIsPages(false);
    if (iscurrentState !== "Dashboard") setIsDashboard(false);
    if (iscurrentState !== "AssetGuard") setIsAssetGuard(false);
    if (iscurrentState !== "FleetGuard") setIsFleetGuard(false);
    if (iscurrentState !== "Payroll") setIsPayroll(false);
    if (iscurrentState !== "DMS") setIsDMS(false);

    // FMS — collapse all sub-groups when leaving the FMS section
    if (!iscurrentState.startsWith("FMS")) {
      setIsFMS(false);
      setIsFMSAccounting(false);
      setIsFMSBudget(false);
      setIsFMSCollection(false);
      setIsFMSDisbursement(false);
    }

    // HRIS — collapse the outer accordion AND all inner sub-groups
    // when the user navigates away from any HRIS page.
    // Guard against "HRISPriv*" so the private accordion isn't collapsed here.
    if (!iscurrentState.startsWith("HRIS") || iscurrentState.startsWith("HRISPriv")) {
      setIsHRIS(false);
      setIsHRISSelfService(false);
      setIsHRISHR(false);
      setIsHRISRecruitment(false);
      setIsHRISWorkforce(false);
      setIsHRISTimekeeping(false);
    }

    // Procurement — collapse all sub-groups when leaving the Procurement section
    if (!iscurrentState.startsWith("Proc")) {
      setIsProcurement(false);
      setIsProcPreProcurement(false);
      setIsProcBacManagement(false);
      setIsProcAdvertisement(false);
      setIsProcBidEvaluation(false);
      setIsProcPostQualification(false);
      setIsProcAwardContract(false);
      setIsProcImplementation(false);
    }
  }, [history, iscurrentState]);

  // ── Build FMS items ─────────────────────────────────────────────────────
  const fmsMenuItems = buildFmsMenuItems({
    isFMSAccounting, setIsFMSAccounting,
    isFMSBudget, setIsFMSBudget,
    isFMSCollection, setIsFMSCollection,
    isFMSDisbursement, setIsFMSDisbursement,
    setIscurrentState,
  });

  // ── Build HRIS items ──────────────────────────────────────────────
  const hrisMenuItems = buildHRISMenuItems({
    isHRISSelfService, setIsHRISSelfService,
    isHRISHR, setIsHRISHR,
    isHRISRecruitment, setIsHRISRecruitment,
    isHRISWorkforce, setIsHRISWorkforce,
    isHRISTimekeeping, setIsHRISTimekeeping,
    setIscurrentState,
  });

  // ── Build Procurement items ─────────────────────────────────────────────
  const procurementMenuItems = buildProcurementMenuItems({
    isProcPreProcurement, setIsProcPreProcurement,
    isProcBacManagement, setIsProcBacManagement,
    isProcAdvertisement, setIsProcAdvertisement,
    isProcBidEvaluation, setIsProcBidEvaluation,
    isProcPostQualification, setIsProcPostQualification,
    isProcAwardContract, setIsProcAwardContract,
    isProcImplementation, setIsProcImplementation,
    setIscurrentState,
  });

  // ── withToggle — wires outer accordion state onto top-level menu items ──
  function withToggle(items, stateVar, setter, stateKey) {
    return items.map(function (item) {
      if (!item.subItems) return item;
      return Object.assign({}, item, {
        stateVariables: stateVar,
        click: function (e) {
          e.preventDefault();
          setter(!stateVar);
          setIscurrentState(stateKey);
        },
      });
    });
  }

  // ── Assemble full menu ──────────────────────────────────────────────────
  const SYSTEMS_HEADER_IDX = 1;
  const beforeSystems = coreMenuItems.slice(0, SYSTEMS_HEADER_IDX + 1);
  const afterSystems = coreMenuItems.slice(SYSTEMS_HEADER_IDX + 1);

  const menuItems = [
    ...beforeSystems,
    ...withToggle(assetGuardMenuItems, isAssetGuard, setIsAssetGuard, "AssetGuard"),
    ...withToggle(fleetGuardMenuItems, isFleetGuard, setIsFleetGuard, "FleetGuard"),
    ...withToggle(dmsMenuItems, isDMS, setIsDMS, "DMS"),
    ...withToggle(fmsMenuItems, isFMS, setIsFMS, "FMS"),
    ...withToggle(hrisMenuItems, isHRIS, setIsHRIS, "HRIS"),
    ...withToggle(payrollMenuItems, isPayroll, setIsPayroll, "Payroll"),
    ...withToggle(procurementMenuItems, isProcurement, setIsProcurement, "Procurement"),
    ...afterSystems,
  ];

  // ── Permission filter ───────────────────────────────────────────────────
  const filterMenu = (items) => {
    return items
      .filter(item => hasAccess(item.permissionTypeId, accessRights))
      .map(item => {
        const newItem = { ...item };
        if (item.subItems) {
          newItem.subItems = filterMenu(item.subItems);
          if (newItem.subItems.length === 0) return null;
        }
        if (item.childItems) {
          newItem.childItems = filterMenu(item.childItems);
          if (newItem.childItems.length === 0) return null;
        }
        return newItem;
      })
      .filter(Boolean);
  };

  return <React.Fragment>{filterMenu(menuItems)}</React.Fragment>;
};

export default Navdata;